import {
  FiHome,
  FiUser,
  FiGrid,
  FiUsers,
  FiSettings,
  FiCalendar,
  FiMessageSquare,
  FiPlusCircle,
  FiList,
  FiShield,
  FiFileText,
  FiMail,
  FiMap,
  FiMapPin,
  FiBell,
  FiGlobe,
  FiImage,
  FiPackage,
  FiSearch,
  FiBriefcase,
} from 'react-icons/fi';

/**
 * Admin + partner sidebar items. Headings stay fixed.
 * Visibility is filtered per role. Order, pin, and quick access can be customized by admin and partner.
 */
export const ADMIN_NAV_GROUPS = [
  {
    id: 'main',
    title: 'Main',
    items: [
      { id: 'dashboard', path: 'dashboard', label: 'Dashboard', icon: FiHome },
    ],
  },
  {
    id: 'real-estate',
    title: 'Real Estate',
    items: [
      { id: 'properties', path: 'properties', label: 'All Listings', icon: FiList },
      { id: 'my-listings', path: 'my-listings', label: 'My Listings', icon: FiFileText },
      { id: 'companies', path: 'companies', label: 'Company List', icon: FiBriefcase, adminOnly: true },
      { id: 'awaiting-listings', path: 'awaiting-listings', label: 'Awaiting Approval', icon: FiGrid },
      { id: 'sold-out-properties', path: 'sold-out-properties', label: 'Sold Out Properties', icon: FiPackage },
      { id: 'create-listing', path: 'create-listing', label: 'Create New Listing', icon: FiPlusCircle },
    ],
  },
  {
    id: 'chat',
    title: 'Chat',
    items: [
      { id: 'chat-inbox', path: 'chat', label: 'User Chat', icon: FiMessageSquare, adminOnly: true, adminUserChat: true },
      { id: 'unknown-user', path: 'unknown-user', label: 'Unknown User', icon: FiUser, adminOnly: true, adminUserChat: true },
      { id: 'partner-chat', path: 'partner-chat', label: 'Partner Chat', icon: FiMessageSquare, adminOnly: true },
      { id: 'chat-configuration', path: 'chat-configuration', label: 'Chat Configuration', icon: FiSettings, adminOnly: true },
      { id: 'partner-chat-inbox', path: 'chat', label: 'Chat Inbox', icon: FiMessageSquare, partnerChat: true },
      { id: 'partner-user-chat', path: 'user-chat', label: 'User Chat', icon: FiMessageSquare, partnerUserChat: true },
    ],
  },
  {
    id: 'map',
    title: 'Map',
    items: [
      { id: 'property-locations-map', path: 'property-locations-map', label: 'All Property Locations', icon: FiMap },
    ],
  },
  {
    id: 'own-portfolio',
    title: 'Own Portfolio',
    ownPortfolio: true,
    items: [
      { id: 'my-properties', path: 'my-properties', label: 'Own Properties', icon: FiGrid },
      { id: 'own-property', path: 'own-property', label: 'Add Own Property', icon: FiPlusCircle },
      { id: 'own-project', path: 'own-project', label: 'Add Own Project', icon: FiPlusCircle },
    ],
  },
  {
    id: 'user-management',
    title: 'User Management',
    adminOnly: true,
    items: [
      { id: 'partners', path: 'partners', label: 'Partner List', icon: FiUsers },
      { id: 'user-management', path: 'user-management', label: 'User Management', icon: FiUsers },
      { id: 'users', path: 'users', label: 'User List', icon: FiUsers },
      { id: 'admins', path: 'admins', label: 'Admin List', icon: FiShield },
      { id: 'config', path: 'config', label: 'Web Config', icon: FiSettings },
      { id: 'config-language', path: 'config-language', label: 'Web Config Edit Language', icon: FiGlobe },
      { id: 'near-location-config', path: 'near-location-config', label: 'Near Location Config', icon: FiMapPin },
      { id: 'hero-backgrounds', path: 'hero-backgrounds', label: 'Hero Background Image', icon: FiImage },
      { id: 'user-kyc', path: 'user-kyc', label: 'User KYC', icon: FiShield },
      { id: 'kyc-document-types', path: 'kyc-document-types', label: 'KYC Document Types', icon: FiFileText },
      { id: 'partner-kyc-requests', path: 'partner-kyc-requests', label: 'Partner KYC Requests', icon: FiFileText },
      { id: 'notification-management', path: 'notification-management', label: 'Notification Management', icon: FiBell },
      { id: 'localization', path: 'localization', label: 'Localization', icon: FiGlobe },
      { id: 'search-auto-suggestions', path: 'search-auto-suggestions', label: 'Search Auto Suggestions', icon: FiSearch },
      { id: 'property-list-per-page', path: 'property-list-per-page', label: 'Property List Per Page', icon: FiList },
      { id: 'security-settings', path: 'security-settings', label: 'Security Settings', icon: FiShield },
    ],
  },
  {
    id: 'detail-config',
    title: 'Detail Config',
    adminOnly: true,
    items: [
      { id: 'property-detail-config', path: 'property-detail-config', label: 'Property Detail Config', icon: FiFileText },
      { id: 'project-detail-config', path: 'project-detail-config', label: 'Project Detail Config', icon: FiFileText },
    ],
  },
  {
    id: 'actions',
    title: null,
    items: [
      { id: 'bookings', path: 'bookings', label: 'Booking List', icon: FiCalendar },
      { id: 'enquiries', path: 'enquiries', label: 'Enquiry List', icon: FiMessageSquare },
      { id: 'user-web-contacts', path: 'user-web-contacts', label: 'User Web Contact List', icon: FiMail },
      { id: 'partner-requests', path: 'partner-requests', label: 'Partner Requests', icon: FiMessageSquare },
    ],
  },
];

export function visibleNavGroups({ isAdmin, isPartner, partnerOwnAccess, partnerChatAccess, partnerUserChatAccess, adminUserChatAccess = true }) {
  return ADMIN_NAV_GROUPS
    .filter((group) => {
      if (group.adminOnly && !isAdmin) return false;
      if (group.ownPortfolio && isPartner && !partnerOwnAccess) return false;
      return true;
    })
    .map((group) => ({
      ...group,
      title: group.id === 'actions' ? (isPartner ? 'Actions' : 'Admin Actions') : group.title,
      items: group.items.filter((item) => {
        if (item.partnerUserChat) return Boolean(isPartner && partnerChatAccess && partnerUserChatAccess);
        if (item.partnerChat) return Boolean(isPartner && partnerChatAccess);
        if (item.adminUserChat) return Boolean(isAdmin && adminUserChatAccess);
        if (item.adminOnly) return isAdmin;
        return true;
      }),
    }))
    .filter((group) => group.items.length > 0);
}

export function allVisibleItems(ctx) {
  return visibleNavGroups(ctx).flatMap((group) => group.items);
}
